import { proxy } from 'valtio'
import { derive, devtools } from 'valtio/utils'
import { type ApiCallType } from '../types/ApiCallType'
import { GlobalSortDirectionEnum } from '../types/GlobalSortDirectionEnum'
import { type TransactionType } from '../types/GlobalTranactionTypeEnum'
import { DEFAULT_LIMIT } from '../utils/apiUtils'
import { type GlobalTransactionStatus } from '../types/GlobalTransactionStatus'

export enum TableColumnsEnum {
  TransactedAt = 'transactedAt',
  Id = 'id',
  InvoiceNumber = 'invoiceNumber',
  CompanyName = 'companyName',
  Name = 'firstName',
  Address = 'addressOne',
  Type = 'ppSslTransactionType',
  Status = 'status',
  Amount = 'amountCents'
}

export interface Pagination {
  totalCount: number
  limit: number
  offset: number
}

export interface Transaction {
  id: number
  addressOne: string
  addressTwo: string
  amountCents: number
  amountCurrencyType: string
  cardId: null | number
  cardName: null | string
  city: string
  companyName: string
  customerId: number | null
  email: null | string
  errorMessage: null | string
  firstName: string
  invoiceNumber: string | null
  lastName: string
  notes: string | null
  relatedTxnId: null | number
  sslTxnId: null | string
  sslCardNumber: string
  ppSslCardShortDescription: string
  ppSslTransactionType: TransactionType
  remainingAmountCents: number
  remainingAmountCurrencyType: string
  state: string
  status: GlobalTransactionStatus
  transactedAt: string
  postalCode: string
}

export interface Filters {
  endDate: Date | null
  offset: number
  search: string
  sortColumn: TableColumnsEnum
  sortDirection: GlobalSortDirectionEnum
  startDate: Date | null
  transactionTypes: readonly TransactionType[]
}

export interface TransactionsProxyType {
  apiCall: ApiCallType
  filters: Filters
  transactions: Transaction[]
  exportTransactions: Transaction[]
  pagination: Pagination
  hasAppliedFilters: boolean
  hasMore: boolean
}

const initialValues = {
  apiCall: {
    error: null,
    loading: false
  },
  exportTransactions: [],
  filters: {
    endDate: null,
    offset: 0,
    search: '',
    sortColumn: TableColumnsEnum.TransactedAt,
    sortDirection: GlobalSortDirectionEnum.Descending,
    startDate: null,
    transactionTypes: []
  },
  pagination: {
    limit: DEFAULT_LIMIT,
    offset: 0,
    totalCount: 0
  },
  transactions: []
}

const baseProxy = proxy(initialValues)

export const transactionsProxy = derive({
  hasAppliedFilters: get => {
    const { filters } = get(baseProxy)
    return Boolean(
      filters.endDate ||
      filters.search.length > 0 ||
      filters.startDate ||
      filters.transactionTypes.length > 0
    )
  },
  hasMore: get => {
    const { transactions, pagination } = get(baseProxy)
    const totalCount = pagination?.totalCount ?? 0
    return transactions.length < totalCount
  }
}, { proxy: baseProxy }) as TransactionsProxyType

devtools(transactionsProxy, { name: 'transactionsProxy' })
